/**
 * Documentation Store
 *
 * Zustand store for managing documentation state including
 * search, panel visibility, and engineering reference content.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type { IDDSchema } from '@/idd/types';
import type {
  DocsSearchResult,
  ObjectDocumentation,
  FieldDocumentation,
  EngineeringDocument,
  DocsPanelState,
} from '../types';
import {
  initializeSearchIndex,
  searchDocs as searchDocsService,
  searchDocsFiltered,
  isSearchIndexReady,
} from '../services/searchIndex';
import {
  getEngineeringDocs,
  getEngineeringDocsForObject,
} from '../services/engineeringReference';
import {
  getInputOutputDocs,
  getInputOutputDocsForObject,
} from '../services/inputOutputReference';

interface DocsState {
  /** Whether the documentation panel is open */
  isPanelOpen: boolean;

  /** Current object type being viewed */
  currentObjectType: string | null;

  /** Current field being viewed */
  currentFieldName: string | null;

  /** Active tab in the panel */
  activeTab: 'reference' | 'input-output' | 'engineering' | 'examples';

  /** Whether the search index is ready */
  isSearchReady: boolean;

  /** Number of indexed documents */
  indexedDocCount: number;

  /** Engineering reference documents */
  engineeringDocs: Map<string, EngineeringDocument>;

  /** Input-Output reference documents */
  inputOutputDocs: Map<string, EngineeringDocument>;

  /** Loading state for async operations */
  isLoading: boolean;

  // Actions

  /** Open the documentation panel */
  openPanel: (objectType?: string, fieldName?: string) => void;

  /** Close the documentation panel */
  closePanel: () => void;

  /** Toggle the documentation panel */
  togglePanel: () => void;

  /** Set the current object type to view */
  setCurrentObject: (objectType: string | null, fieldName?: string | null) => void;

  /** Set the active tab */
  setActiveTab: (tab: 'reference' | 'input-output' | 'engineering' | 'examples') => void;

  /** Initialize the search index with IDD schema */
  initializeSearch: (schema: IDDSchema) => Promise<void>;

  /** Search documentation */
  searchDocs: (query: string, limit?: number) => DocsSearchResult[];

  /** Search with filters */
  searchDocsFiltered: (
    query: string,
    options?: { type?: 'object' | 'field' | 'engineering'; group?: string; limit?: number }
  ) => DocsSearchResult[];

  /** Get object documentation from IDD schema */
  getObjectDocumentation: (
    objectType: string,
    schema: IDDSchema
  ) => ObjectDocumentation | null;

  /** Get field documentation */
  getFieldDocumentation: (
    objectType: string,
    fieldName: string,
    schema: IDDSchema
  ) => FieldDocumentation | null;

  /** Add engineering reference document */
  addEngineeringDoc: (doc: EngineeringDocument) => void;

  /** Get engineering reference for an object type */
  getEngineeringRef: (objectType: string) => EngineeringDocument | null;

  /** Get all engineering docs related to an object */
  getEngineeringDocsForObject: (objectType: string) => EngineeringDocument[];

  /** Get input-output reference for an object type */
  getInputOutputRef: (objectType: string) => EngineeringDocument | null;

  /** Get all input-output docs related to an object */
  getInputOutputDocsForObject: (objectType: string) => EngineeringDocument[];

  /** Get related objects for an object type */
  getRelatedObjects: (objectType: string, schema: IDDSchema) => string[];

  /** Load engineering reference documents into the store */
  loadEngineeringDocs: () => void;

  /** Load input-output reference documents into the store */
  loadInputOutputDocs: () => void;
}

export const useDocsStore = create<DocsState>()(
  devtools(
    (set, get) => ({
      isPanelOpen: false,
      currentObjectType: null,
      currentFieldName: null,
      activeTab: 'reference',
      isSearchReady: false,
      indexedDocCount: 0,
      engineeringDocs: new Map(),
      inputOutputDocs: new Map(),
      isLoading: false,

      openPanel: (objectType?: string, fieldName?: string) => {
        set({
          isPanelOpen: true,
          currentObjectType: objectType || null,
          currentFieldName: fieldName || null,
        });
      },

      closePanel: () => {
        set({ isPanelOpen: false });
      },

      togglePanel: () => {
        set((state) => ({ isPanelOpen: !state.isPanelOpen }));
      },

      setCurrentObject: (objectType: string | null, fieldName?: string | null) => {
        set({
          currentObjectType: objectType,
          currentFieldName: fieldName ?? null,
        });
      },

      setActiveTab: (tab: 'reference' | 'input-output' | 'engineering' | 'examples') => {
        set({ activeTab: tab });
      },

      initializeSearch: async (schema: IDDSchema) => {
        set({ isLoading: true });
        try {
          // Load engineering docs first if not already loaded
          const { loadEngineeringDocs, loadInputOutputDocs, engineeringDocs, inputOutputDocs } = get();
          if (engineeringDocs.size === 0) {
            loadEngineeringDocs();
          }
          if (inputOutputDocs.size === 0) {
            loadInputOutputDocs();
          }

          // Get the updated docs after loading
          const updatedEngineeringDocs = get().engineeringDocs;
          const updatedInputOutputDocs = get().inputOutputDocs;

          // Combine all docs for indexing
          const allDocs = [
            ...Array.from(updatedEngineeringDocs.values()),
            ...Array.from(updatedInputOutputDocs.values()),
          ];

          const count = await initializeSearchIndex(schema, allDocs);
          set({
            isSearchReady: true,
            indexedDocCount: count,
            isLoading: false,
          });
        } catch (error) {
          console.error('Failed to initialize search index:', error);
          set({ isLoading: false });
        }
      },

      searchDocs: (query: string, limit = 20): DocsSearchResult[] => {
        if (!isSearchIndexReady()) return [];
        return searchDocsService(query, limit);
      },

      searchDocsFiltered: (
        query: string,
        options = {}
      ): DocsSearchResult[] => {
        if (!isSearchIndexReady()) return [];
        return searchDocsFiltered(query, options);
      },

      getObjectDocumentation: (
        objectType: string,
        schema: IDDSchema
      ): ObjectDocumentation | null => {
        const objDef = schema.objectTypes.get(objectType.toLowerCase());
        if (!objDef) return null;

        const { engineeringDocs, inputOutputDocs } = get();
        const engDoc = engineeringDocs.get(objectType.toLowerCase());
        const ioDoc = inputOutputDocs.get(objectType.toLowerCase());

        const fields: FieldDocumentation[] = objDef.fields.map((f) => ({
          id: f.id,
          name: f.name || f.id,
          type: f.type,
          required: f.required,
          memo: f.memo,
          default: f.default,
          units: f.units,
          minimum: f.minimum,
          maximum: f.maximum,
          choices: f.choices,
          objectList: f.objectList,
          autosizable: f.autosizable,
          autocalculatable: f.autocalculatable,
        }));

        const relatedObjects = get().getRelatedObjects(objectType, schema);

        return {
          name: objDef.name,
          group: objDef.group,
          memo: objDef.memo,
          fields,
          engineeringContent: engDoc?.content,
          inputOutputContent: ioDoc?.content,
          examples: [],
          relatedObjects,
          isUnique: objDef.isUnique,
          isRequired: objDef.isRequired,
        };
      },

      getFieldDocumentation: (
        objectType: string,
        fieldName: string,
        schema: IDDSchema
      ): FieldDocumentation | null => {
        const objDef = schema.objectTypes.get(objectType.toLowerCase());
        if (!objDef) return null;

        const field = objDef.fields.find(
          (f) =>
            f.name?.toLowerCase() === fieldName.toLowerCase() ||
            f.id.toLowerCase() === fieldName.toLowerCase()
        );
        if (!field) return null;

        return {
          id: field.id,
          name: field.name || field.id,
          type: field.type,
          required: field.required,
          memo: field.memo,
          default: field.default,
          units: field.units,
          minimum: field.minimum,
          maximum: field.maximum,
          choices: field.choices,
          objectList: field.objectList,
          autosizable: field.autosizable,
          autocalculatable: field.autocalculatable,
        };
      },

      addEngineeringDoc: (doc: EngineeringDocument) => {
        set((state) => {
          const newDocs = new Map(state.engineeringDocs);
          newDocs.set(doc.id.toLowerCase(), doc);
          return { engineeringDocs: newDocs };
        });
      },

      getEngineeringRef: (objectType: string): EngineeringDocument | null => {
        const { engineeringDocs } = get();
        return engineeringDocs.get(objectType.toLowerCase()) || null;
      },

      getEngineeringDocsForObject: (objectType: string): EngineeringDocument[] => {
        return getEngineeringDocsForObject(objectType);
      },

      loadEngineeringDocs: () => {
        const docs = getEngineeringDocs();
        set((state) => {
          const newDocs = new Map(state.engineeringDocs);
          for (const doc of docs) {
            newDocs.set(doc.id.toLowerCase(), doc);
            // Also map by related objects for easier lookup
            for (const relatedObj of doc.relatedObjects) {
              if (!newDocs.has(relatedObj.toLowerCase())) {
                newDocs.set(relatedObj.toLowerCase(), doc);
              }
            }
          }
          return { engineeringDocs: newDocs };
        });
        console.log(`Loaded ${String(docs.length)} engineering reference documents`);
      },

      getInputOutputRef: (objectType: string): EngineeringDocument | null => {
        const { inputOutputDocs } = get();
        return inputOutputDocs.get(objectType.toLowerCase()) || null;
      },

      getInputOutputDocsForObject: (objectType: string): EngineeringDocument[] => {
        return getInputOutputDocsForObject(objectType);
      },

      loadInputOutputDocs: () => {
        const docs = getInputOutputDocs();
        set((state) => {
          const newDocs = new Map(state.inputOutputDocs);
          for (const doc of docs) {
            newDocs.set(doc.id.toLowerCase(), doc);
            // Also map by related objects for easier lookup
            for (const relatedObj of doc.relatedObjects) {
              if (!newDocs.has(relatedObj.toLowerCase())) {
                newDocs.set(relatedObj.toLowerCase(), doc);
              }
            }
          }
          return { inputOutputDocs: newDocs };
        });
        console.log(`Loaded ${String(docs.length)} input-output reference documents`);
      },

      getRelatedObjects: (objectType: string, schema: IDDSchema): string[] => {
        const objDef = schema.objectTypes.get(objectType.toLowerCase());
        if (!objDef) return [];

        const related = new Set<string>();

        // Find objects referenced by this object's fields
        for (const field of objDef.fields) {
          if (field.objectList) {
            const referencedTypes = schema.referenceLists.get(field.objectList);
            if (referencedTypes) {
              for (const typeName of referencedTypes) {
                if (typeName.toLowerCase() !== objectType.toLowerCase()) {
                  related.add(typeName);
                }
              }
            }
          }
        }

        // Find objects that reference this object
        for (const field of objDef.fields) {
          if (field.references) {
            for (const refListName of field.references) {
              // Find objects that have fields using this reference list
              for (const [, otherObj] of schema.objectTypes) {
                if (otherObj.name.toLowerCase() === objectType.toLowerCase()) continue;
                for (const otherField of otherObj.fields) {
                  if (otherField.objectList === refListName) {
                    related.add(otherObj.name);
                    break;
                  }
                }
              }
            }
          }
        }

        // Limit to 10 most relevant
        return Array.from(related).slice(0, 10);
      },
    }),
    { name: 'DocsStore' }
  )
);

/**
 * Selector hooks
 */
export const useDocsPanelState = (): DocsPanelState => {
  return useDocsStore(
    useShallow((state) => ({
      isOpen: state.isPanelOpen,
      objectType: state.currentObjectType,
      fieldName: state.currentFieldName,
      activeTab: state.activeTab,
    }))
  );
};

export const useIsSearchReady = () => {
  return useDocsStore((state) => state.isSearchReady);
};
